import { formatApproximateDistance, haversineDistanceKm } from '@/features/location/distance';
import type { PublicStore } from '@/features/menu/types';
import type { BrowseStore } from '@/state/browseStoreStore';

export interface StoreDistanceLocation {
  latitude: number;
  longitude: number;
}

export interface StoreRow extends PublicStore {
  distanceKm?: number;
  apiIndex: number;
}

export function composedStoreAddress(store: Pick<PublicStore, 'address' | 'addressLine' | 'communeName' | 'provinceName'>): string | undefined {
  const value = [store.addressLine, store.communeName, store.provinceName]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(', ');
  return value || store.address?.trim() || undefined;
}

/** Keeps server/API order stable for all distance ties and unavailable distances. */
export function sortStoreRows(
  stores: PublicStore[],
  options: {
    orderingStoreCode?: string;
    selectedStoreCode?: string;
    customerLocation?: StoreDistanceLocation;
  }
): StoreRow[] {
  const normalized = (value?: string) => value?.trim().toUpperCase();
  const orderingCode = normalized(options.orderingStoreCode);
  const selectedCode = normalized(options.selectedStoreCode);
  const hasLocation = Boolean(options.customerLocation);

  return stores
    .map((store, apiIndex) => ({
      ...store,
      apiIndex,
      distanceKm: options.customerLocation
        ? haversineDistanceKm(options.customerLocation, store)
        : undefined,
    }))
    .sort((left, right) => {
      const rank = (row: StoreRow) => {
        const code = normalized(row.storeCode);
        if (orderingCode && code === orderingCode) return 0;
        if (selectedCode && code === selectedCode) return 1;
        if (!hasLocation) return 2;
        if (row.distanceKm === undefined) return 4;
        return row.acceptingOrders ? 2 : 3;
      };
      const rankDelta = rank(left) - rank(right);
      if (rankDelta !== 0) return rankDelta;
      if (hasLocation && left.distanceKm !== undefined && right.distanceKm !== undefined) {
        const distanceDelta = left.distanceKm - right.distanceKm;
        if (distanceDelta !== 0) return distanceDelta;
      }
      return left.apiIndex - right.apiIndex;
    });
}

export function browseStoreDistance(
  store: BrowseStore | null | undefined,
  customerLocation?: StoreDistanceLocation
): string | undefined {
  if (!store || !customerLocation) return undefined;
  return formatApproximateDistance(haversineDistanceKm(customerLocation, store));
}
